import React, { useMemo } from "react";
import styled from "styled-components";
import { primaryColor } from "../../styles/color";
import { fontFamily } from "../../styles/theme";
import { priceFormatter } from "../../../../helper/priceFormatter";

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${fontFamily.font};
  font-size: 0.9rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 10px;
  border-bottom: 2px solid #e5e7eb;
  color: #374151;
  font-weight: 600;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f3f4f6;
  color: #4b5563;
  text-align: center;
  &:first-child {
    text-align: left;
    font-weight: 500;
    color: #111827;
  }
`;

const Check = styled.span`
  color: ${primaryColor};
  font-weight: 700;
`;

const DEFAULT_FEATURES = [
  { name: "Create profile & portfolio", start: true, grow: true, advance: true },
  { name: "Browse projects", start: true, grow: true, advance: true },
  { name: "Apply to projects", start: false, grow: true, advance: true },
  { name: "Unlimited services", start: false, grow: true, advance: true },
  { name: "Featured profile", start: false, grow: true, advance: true },
  { name: "Priority visibility", start: false, grow: true, advance: true },
  { name: "Top search placement", start: false, grow: false, advance: true },
  { name: "Verified badge eligibility", start: false, grow: false, advance: true },
  { name: "Advanced analytics", start: false, grow: true, advance: true },
];

function planColumnKey(planName = "") {
  const s = String(planName).toLowerCase();
  if (s.includes("advance")) return "advance";
  if (s.includes("grow")) return "grow";
  return "start";
}

function buildFromPlans(plans) {
  const cols = (Array.isArray(plans) ? plans : [])
    .filter((p) => p?.is_active !== false)
    .slice(0, 3);
  if (cols.length < 2) return null;

  const titles = new Set();
  cols.forEach((p) => {
    (p.subscription_inclusions || []).forEach((inc) => {
      if (inc?.title && inc.is_active !== false) titles.add(String(inc.title).trim());
    });
  });
  if (!titles.size) return null;

  return {
    columns: cols,
    rows: [...titles].map((name) => {
      const row = { name, start: false, grow: false, advance: false };
      cols.forEach((p) => {
        const key = planColumnKey(p.plan || p.plan_name);
        const has = (p.subscription_inclusions || []).some(
          (inc) => String(inc?.title || "").trim() === name && inc.is_active !== false,
        );
        row[key] = has;
      });
      return row;
    }),
  };
}

export function SubscriptionComparisonTable({ monthly = true, plans = [] }) {
  const built = useMemo(() => buildFromPlans(plans), [plans]);
  const rows = built?.rows || DEFAULT_FEATURES;
  const columns = built?.columns || [];

  const prices = useMemo(() => {
    if (columns.length >= 3) {
      const sorted = [...columns].sort((a, b) => Number(a.rate) - Number(b.rate));
      return {
        start: `AED ${priceFormatter(sorted[0]?.rate ?? "0")}`,
        grow: `AED ${priceFormatter(sorted[1]?.rate ?? "0")}`,
        advance: `AED ${priceFormatter(sorted[2]?.rate ?? "0")}`,
      };
    }
    return monthly
      ? { start: "AED 0", grow: "AED 69.99", advance: "AED 146.99" }
      : { start: "AED 0", grow: "AED 798.99", advance: "AED 1,598.99" };
  }, [columns, monthly]);

  const period = monthly ? "/mo" : "/yr";

  return (
    <Table>
      <thead>
        <tr>
          <Th>Feature</Th>
          <Th>Liber Start<br /><small>{prices.start}{period}</small></Th>
          <Th>Liber Grow<br /><small>{prices.grow}{period}</small></Th>
          <Th>Liber Advance<br /><small>{prices.advance}{period}</small></Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((f) => (
          <tr key={f.name}>
            <Td>{f.name}</Td>
            <Td>{f.start ? <Check>✓</Check> : "—"}</Td>
            <Td>{f.grow ? <Check>✓</Check> : "—"}</Td>
            <Td>{f.advance ? <Check>✓</Check> : "—"}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export default SubscriptionComparisonTable;
